import React from "react";
import { useSearchParams } from "react-router-dom";
import {
  User,
  Shield,
  PanelLeftClose,
} from "lucide-react";
import { cn } from "lib/utils";
import { Button } from "components/ui/button";
import { ScrollArea } from "components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/tooltip";

export const SETTINGS_TABS = [
  {
    key: "profile",
    label: "Profile",
    description: "Personal details and public presence",
    icon: User,
  },
  {
    key: "account",
    label: "Account & Security",
    description: "Credentials, passwords, and sessions",
    icon: Shield,
  },
];

const SettingsSidebar = ({ collapsed, onToggle }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "profile";

  const onTabChange = (key) => {
    if (key) {
      setSearchParams({ tab: key });
    }
  };

  return (
    <aside
      className={cn(
        collapsed
          ? "w-0 border-none opacity-0 overflow-hidden px-0"
          : "w-[260px] opacity-100",
        "flex flex-col bg-white border-r border-gray-200 text-gray-700 h-full flex-shrink-0 transition-all duration-300 relative select-none"
      )}
    >
      <TooltipProvider delayDuration={0}>
        {/* Header - height 60px matching main layout */}
        <div className="flex h-[60px] items-center justify-between px-4 shrink-0 min-w-[260px] border-b border-gray-100">
          <div className="flex items-center gap-2 overflow-hidden">
            <span className="font-semibold text-gray-900 truncate">Settings</span>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onToggle}
                className="text-gray-500 hover:text-gray-900 hover:bg-gray-100 h-8 w-8"
              >
                <PanelLeftClose size={18} />
              </Button>
            </TooltipTrigger>
            <TooltipContent
              side="right"
              className="z-[9999] bg-gray-900 text-white border-none shadow-lg py-1 px-2.5 text-xs"
            >
              Collapse sidebar
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Scrollable Navigation Body */}
        <ScrollArea className="flex-1 px-3 py-4 min-w-[260px]">
          <div className="flex flex-col gap-1">
            <span className="px-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
              Preferences
            </span>

            {SETTINGS_TABS.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.key;

              return (
                <Button
                  key={item.key}
                  variant={isActive ? "secondary" : "ghost"}
                  className={cn(
                    "w-full justify-start gap-3 h-10 text-sm font-normal px-2.5 rounded-lg transition-all",
                    isActive
                      ? "bg-gray-100 text-gray-900 font-medium shadow-2xs"
                      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                  )}
                  onClick={() => onTabChange(item.key)}
                >
                  <div
                    className={cn(
                      "flex items-center justify-center w-7 h-7 rounded-md",
                      isActive
                        ? "bg-white text-gray-900 shadow-2xs border border-gray-200/60"
                        : "text-gray-500"
                    )}
                  >
                    <Icon size={16} />
                  </div>
                  <div className="flex flex-col items-start min-w-0 text-left">
                    <span className="truncate leading-none text-sm font-medium">{item.label}</span>
                  </div>
                </Button>
              );
            })}
          </div>
        </ScrollArea>
      </TooltipProvider>
    </aside>
  );
};

export default SettingsSidebar;
